import { BalanceData, Command, CommandKind, CommandResult, GearBonus, LevelModifiers, LevelModifiersJson, LevelPhase, LevelSim, NO_MODIFIERS, ReplayError, isPlainModifiers, modifiersFromJson, modifiersToJson, runReplay, RunTally, ScriptedPlayer, SimConstants, SimEventKind, TimedCommand } from "@highmarch/domain";

/**
 * The state right after the step that cleared a stage, before any command of that tick: where a checkpoint retry
 * (suspend.ts) restarts. Kept for every stage cleared; `hash` lets the rebuilt run prove it is the same run.
 */
export interface StageMark {
  readonly stagesCleared: number;
  readonly tick: number;
  readonly hash: string;
  readonly kills: number;
  readonly leaks: number;
}

/** Drives one level: owns the sim, records every accepted command with its tick, exposes the sim read-only. */
export class LevelSession {
  readonly sim: LevelSim;
  private readonly log: TimedCommand[] = [];
  private readonly owned: boolean[];
  /** What the run did, for the stars' challenge (sim/stars.ts); fed every accepted command. */
  readonly tally = new RunTally();
  /** Leaks so far per enemy archetype index (the defeat screen's "what got through"); presentation only, not hashed. */
  private readonly leakCounts: number[];
  /** Stage marks by stages cleared (index 0 unused): presentation and checkpoint bookkeeping, never hashed. */
  private readonly marks: StageMark[] = [];
  /**
   * Checkpoint retries this attempt has spent (DESIGN §4.8.8: one per attempt, one more with a War Banner, MP-06).
   * Carried through suspend and resume; a fresh attempt starts at 0.
   */
  checkpointsUsed = 0;
  /** This attempt has restarted from a checkpoint at least once. */
  get checkpointUsed(): boolean { return this.checkpointsUsed > 0; }
  set checkpointUsed(v: boolean) { this.checkpointsUsed = v ? Math.max(1, this.checkpointsUsed) : 0; }
  /** Checkpoint retries the attempt may take in all: one, plus the War Banner's. */
  get checkpointsAllowed(): number { return 1 + (this.modifiers.supplies?.checkpoints ?? 0); }
  /**
   * Damage dealt so far per tower id, summed from the EnemyDamaged events (QoL R2: the tower sheet and the end
   * screen); presentation only, not hashed. Kept here rather than in the view so a resumed or undone run replays it.
   */
  private readonly damage = new Map<number, number>();
  /**
   * Kills of raiders that died under a Hex Totem's curse, per totem id (TOWERS_V2): the totem deals no damage, so its
   * mastery and its sheet count these. Presentation only, not hashed.
   */
  private readonly assists = new Map<number, number>();

  constructor(readonly balance: BalanceData, readonly level: number, readonly seed: bigint, ownedTowers: readonly boolean[], readonly towerLevel: number | readonly number[] = 1, readonly firstClear = false, readonly gear: readonly GearBonus[] = [], readonly modifiers: LevelModifiers = NO_MODIFIERS) {
    this.owned = [...ownedTowers];
    this.sim = new LevelSim(balance, level, seed, this.owned, towerLevel, firstClear, gear, modifiers);
    this.leakCounts = balance.enemies.map(() => 0);
  }

  get commands(): readonly TimedCommand[] { return this.log; }
  get ownedTowers(): readonly boolean[] { return this.owned; }
  get leaksByType(): readonly number[] { return this.leakCounts; }
  get damageByTower(): ReadonlyMap<number, number> { return this.damage; }
  get assistsByTower(): ReadonlyMap<number, number> { return this.assists; }
  get isOver(): boolean { return this.sim.isOver; }
  /** The mark left when `stagesCleared` stages had been cleared, or undefined if the run never got there. */
  stageMark(stagesCleared: number): StageMark | undefined { return this.marks[stagesCleared]; }

  execute(command: Command): CommandResult {
    const r = this.sim.execute(command);
    if (r === CommandResult.Ok) { this.log.push({ tick: this.sim.tick, command }); this.tally.note(this.sim, command); }
    return r;
  }

  tick(ticks = 1): void {
    for (let i = 0; i < ticks && !this.sim.isOver; i++) { this.sim.step(); this.countLeaks(); this.markStage(); }
  }

  /** After a step that cleared a stage (and did not end the level): the checkpoint state, before this tick's commands. */
  private markStage(): void {
    const sim = this.sim;
    if (sim.isOver || !sim.events.has(SimEventKind.StageCleared)) return;
    this.marks[sim.stagesCleared] = { stagesCleared: sim.stagesCleared, tick: sim.tick, hash: sim.computeHash().toString(16).padStart(16, "0"), kills: sim.kills, leaks: sim.leaks };
  }

  /**
   * Reads this tick's EnemyDamaged events into the per-tower damage, and its EnemyLeaked events into the per-type tally. The event names the enemy by id; its slot is dead
   * but still holds the archetype, since wave spawns run before movement. A boss minion spawned later in the same tick
   * can take the slot; its id then differs and that one leak goes uncounted, which only thins the defeat screen's tally.
   */
  private countLeaks(): void {
    const events = this.sim.events, slots = this.sim.enemySlots;
    for (let i = 0; i < events.count; i++) {
      const ev = events.at(i);
      if (ev.kind === SimEventKind.EnemyDamaged && ev.b > 0) this.damage.set(ev.b, (this.damage.get(ev.b) ?? 0) + ev.c);
      // a Hex Totem's share: the extra damage its curse added to this hit, and the kill of a cursed raider
      if (ev.kind === SimEventKind.EnemyDamaged || ev.kind === SimEventKind.EnemyKilled) {
        for (const e of slots) {
          if (e.id !== ev.a) continue;
          const hex = e.hexTowerId, bonus = e.hexBonus.toNumber();
          if (hex > 0 && bonus > 0) {
            if (ev.kind === SimEventKind.EnemyKilled) this.assists.set(hex, (this.assists.get(hex) ?? 0) + 1);
            else this.damage.set(hex, (this.damage.get(hex) ?? 0) + (ev.c * bonus) / (1 + bonus));
          }
          break;
        }
      }
      if (ev.kind !== SimEventKind.EnemyLeaked) continue;
      for (const e of slots) if (e.id === ev.a) { this.leakCounts[e.archetype]++; break; }
    }
  }

  /** Re-simulates this session's log headlessly and checks it reproduces the live state hash. */
  verifyReplay(): boolean {
    return runReplay(this.balance, this.level, this.seed, this.owned, this.log, Math.max(this.sim.tick, 1), this.towerLevel, this.firstClear, this.gear, this.modifiers).hash === this.sim.computeHash();
  }

  toRecord(): ReplayRecord {
    return {
      formatVersion: 1, level: this.level, towerLevel: typeof this.towerLevel === "number" ? this.towerLevel : [...this.towerLevel], firstClear: this.firstClear, seed: this.seed.toString(),
      hash: this.sim.computeHash().toString(16).padStart(16, "0"), phase: LevelPhase[this.sim.phase], ticks: this.sim.tick,
      stagesCleared: this.sim.stagesCleared, kills: this.sim.kills, leaks: this.sim.leaks,
      ownedTowers: this.balance.towers.filter((t) => this.owned[t.index]).map((t) => t.id),
      commands: this.log.map((tc) => [tc.tick, CommandKind[tc.command.kind], tc.command.a, tc.command.b, tc.command.c]),
      ...(isPlainModifiers(this.modifiers) ? {} : { modifiers: modifiersToJson(this.balance, this.modifiers) }),
    };
  }
}

/** Serializable replay: inputs plus the expected end state (goldens, result submission). */
export interface ReplayRecord {
  formatVersion: number;
  level: number;
  towerLevel: number | number[];
  firstClear: boolean;
  seed: string;
  hash: string;
  phase: string;
  ticks: number;
  stagesCleared: number;
  kills: number;
  leaks: number;
  ownedTowers: string[];
  commands: [number, string, number, number, number][];
  /** The run's modifiers (DESIGN §5.1); absent for a plain run, which every record before them is. */
  modifiers?: LevelModifiersJson;
}

export function recordOwnedTowers(balance: BalanceData, record: ReplayRecord): boolean[] {
  const owned = balance.towers.map(() => false);
  for (const id of record.ownedTowers) owned[balance.towerIndex(id)] = true;
  return owned;
}

/** The record's modifiers, resolved against this build's affixes; throws ReplayError for ones it cannot play. */
export function recordModifiers(balance: BalanceData, record: ReplayRecord): LevelModifiers {
  if (record.modifiers === undefined) return NO_MODIFIERS;
  const m = modifiersFromJson(balance, record.modifiers);
  if (m === undefined) throw new ReplayError("record has modifiers this build cannot play");
  return m;
}

export function recordCommands(record: ReplayRecord): TimedCommand[] {
  return record.commands.map(([tick, kind, a, b, c]) => ({ tick, command: { kind: CommandKind[kind as keyof typeof CommandKind], a, b, c } }));
}

/** Plays a session to the end with a scripted player. */
export function playToEnd(session: LevelSession, player: ScriptedPlayer, maxTicks = SimConstants.MaxTicksPerLevel): void {
  while (!session.isOver && session.sim.tick < maxTicks) {
    const c = player.next(session.sim);
    if (c) {
      const r = session.execute(c);
      if (r !== CommandResult.Ok) throw new Error(`Scripted player issued an invalid command: ${CommandResult[r]}`);
    } else session.tick();
  }
}
